// © 2026 SoulMD, LLC. All rights reserved.
import React, { useState, useEffect, useCallback } from 'react';

interface Props { API: string; }
type Tab = 'users' | 'analytics' | 'visitors' | 'health' | 'moderation';

const CARD: React.CSSProperties = {background:'rgba(255,255,255,0.85)',borderRadius:'20px',padding:'20px',boxShadow:'0 4px 20px rgba(100,130,200,0.1)',marginBottom:'16px'};
const LABEL: React.CSSProperties = {fontSize:'11px',fontWeight:'700',color:'#8aa0c0',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:'8px'};
const BTN: React.CSSProperties = {border:'1px solid rgba(122,176,240,0.3)',borderRadius:'10px',padding:'6px 12px',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'rgba(255,255,255,0.7)',color:'#4a7ad0'};
const BTN_DANGER: React.CSSProperties = {border:'1px solid rgba(224,80,80,0.3)',borderRadius:'10px',padding:'6px 12px',fontSize:'12px',fontWeight:'600',cursor:'pointer',background:'rgba(253,232,232,0.7)',color:'#c04040'};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {year:'numeric',month:'short',day:'numeric'});
};

const Admin: React.FC<Props> = ({ API }) => {
  const [token, setToken] = useState<string>(() => {
    const t = localStorage.getItem('admin_token') || '';
    const exp = Number(localStorage.getItem('admin_token_expires') || 0);
    if (t && Date.now() < exp) return t;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_token_expires');
    return '';
  });
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('users');

  const login = async () => {
    if (!tokenInput) return;
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API}/admin/verify`, {method:'POST', headers:{'Content-Type':'application/json','X-Admin-Token':tokenInput}});
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Invalid token');
      }
      localStorage.setItem('admin_token', tokenInput);
      localStorage.setItem('admin_token_expires', String(Date.now() + 24*60*60*1000));
      setToken(tokenInput);
    } catch(e:any) { setAuthError(e.message); }
    finally { setAuthLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_token_expires');
    setToken(''); setTokenInput('');
  };

  if (!token) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
        <div style={{background:'rgba(255,255,255,0.85)',borderRadius:'24px',padding:'40px',maxWidth:'400px',width:'100%',boxShadow:'0 8px 32px rgba(100,130,200,0.12)'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'20px'}}>
            <div style={{width:'40px',height:'40px',borderRadius:'12px',background:'linear-gradient(135deg,#7ab0f0,#9b8fe8)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'18px'}}>⚙</div>
            <div>
              <div style={{fontSize:'20px',fontWeight:'800',color:'#1a2a4a'}}>Admin Console</div>
              <div style={{fontSize:'11px',color:'#8aa0c0'}}>Restricted access</div>
            </div>
          </div>
          <div style={{fontSize:'13px',color:'#8aa0c0',marginBottom:'20px',lineHeight:'1.6'}}>Enter your admin token to continue. Session lasts 24 hours.</div>
          {authError && <div style={{background:'#fde8e8',border:'1px solid #f0b0b0',borderRadius:'10px',padding:'12px',fontSize:'13px',color:'#c04040',marginBottom:'16px'}}>{authError}</div>}
          <input type="password" placeholder="Admin token" value={tokenInput} onChange={e=>setTokenInput(e.target.value)} onKeyDown={e=>e.key==='Enter' && login()} style={{width:'100%',padding:'14px',borderRadius:'12px',border:'1px solid rgba(122,176,240,0.3)',fontSize:'14px',color:'#1a2a4a',background:'rgba(240,246,255,0.5)',marginBottom:'16px',outline:'none',boxSizing:'border-box'}}/>
          <button onClick={login} disabled={authLoading || !tokenInput} style={{width:'100%',background:'linear-gradient(135deg,#7ab0f0,#9b8fe8)',border:'none',borderRadius:'14px',padding:'14px',fontSize:'15px',fontWeight:'700',color:'white',cursor:authLoading||!tokenInput?'default':'pointer',opacity:authLoading||!tokenInput?0.6:1}}>
            {authLoading ? 'Verifying...' : 'Access Admin'}
          </button>
        </div>
      </div>
    );
  }

  const authHeaders = { 'Content-Type':'application/json', 'X-Admin-Token': token };

  return (
    <div style={{minHeight:'100vh',padding:'20px 16px',maxWidth:'1200px',margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px',flexWrap:'wrap',gap:'12px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <div style={{width:'40px',height:'40px',borderRadius:'12px',background:'linear-gradient(135deg,#7ab0f0,#9b8fe8)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'18px'}}>⚙</div>
          <div>
            <div style={{fontSize:'20px',fontWeight:'800',color:'#1a2a4a'}}>Admin Console</div>
            <div style={{fontSize:'11px',color:'#8aa0c0'}}>EKGScan · SoulMD</div>
          </div>
        </div>
        <button onClick={logout} style={{...BTN}}>Sign Out</button>
      </div>

      <div style={{display:'flex',gap:'8px',marginBottom:'20px',flexWrap:'wrap'}}>
        {(['users','analytics','visitors','health','moderation'] as Tab[]).map(t => (
          <button key={t} onClick={()=>setTab(t)} style={{background:tab===t?'linear-gradient(135deg,#7ab0f0,#9b8fe8)':'rgba(255,255,255,0.7)',border:'1px solid rgba(122,176,240,0.3)',borderRadius:'10px',padding:'8px 16px',fontSize:'13px',fontWeight:'600',color:tab===t?'white':'#4a7ad0',cursor:'pointer',textTransform:'capitalize'}}>{t}</button>
        ))}
      </div>

      {tab === 'users' && <UsersTab API={API} headers={authHeaders} onUnauthorized={logout}/>}
      {tab === 'analytics' && <AnalyticsTab API={API} headers={authHeaders} onUnauthorized={logout}/>}
      {tab === 'visitors' && <VisitorsTab API={API} headers={authHeaders} onUnauthorized={logout}/>}
      {tab === 'health' && <HealthTab API={API} headers={authHeaders} onUnauthorized={logout}/>}
      {tab === 'moderation' && <ModerationTab API={API} headers={authHeaders} onUnauthorized={logout}/>}

      <div style={{textAlign:'center',fontSize:'11px',color:'#a0b0c8',marginTop:'24px',padding:'16px',lineHeight:'1.6'}}>
        Note: subscription tier changes here update the DB only — they do not cancel or create Stripe subscriptions.
      </div>
    </div>
  );
};

interface TabProps { API: string; headers: Record<string,string>; onUnauthorized: () => void; }

function useAuthedFetch(onUnauthorized: () => void) {
  return useCallback(async (url: string, init?: RequestInit) => {
    const res = await fetch(url, init);
    if (res.status === 401 || res.status === 503) onUnauthorized();
    return res;
  }, [onUnauthorized]);
}

const UsersTab: React.FC<TabProps> = ({ API, headers, onUnauthorized }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [pendingTier, setPendingTier] = useState('');
  const [pendingClinician, setPendingClinician] = useState(false);
  const [pendingSuperuser, setPendingSuperuser] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const af = useAuthedFetch(onUnauthorized);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await af(`${API}/admin/users${qs}`, { headers });
      if (!res.ok) throw new Error('Failed to load users');
      const d = await res.json();
      setUsers(d.users); setTotal(d.total);
    } catch(e:any) { setError(e.message); }
    finally { setLoading(false); }
  }, [API, headers, search, af]);

  useEffect(() => { load(); }, [load]);

  const saveUser = async (id: number) => {
    try {
      const res = await af(`${API}/admin/users/${id}`, {method:'PATCH', headers, body: JSON.stringify({subscription_tier: pendingTier, is_clinician: pendingClinician, is_superuser: pendingSuperuser})});
      if (!res.ok) throw new Error('Update failed');
      setEditing(null);
      load();
    } catch(e:any) { setError(e.message); }
  };

  const deleteUser = async (id: number) => {
    try {
      const res = await af(`${API}/admin/users/${id}`, {method:'DELETE', headers});
      if (!res.ok) throw new Error('Delete failed');
      setConfirmDelete(null);
      load();
    } catch(e:any) { setError(e.message); }
  };

  return (
    <div>
      <div style={{...CARD, display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center'}}>
        <input placeholder="Search by email..." value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==='Enter' && load()} style={{flex:1,minWidth:'200px',padding:'10px 14px',borderRadius:'10px',border:'1px solid rgba(122,176,240,0.3)',fontSize:'13px',background:'rgba(240,246,255,0.5)',outline:'none'}}/>
        <button onClick={load} style={{...BTN}}>Refresh</button>
        <div style={{fontSize:'12px',color:'#8aa0c0'}}>{total} total</div>
      </div>

      {error && <div style={{...CARD, background:'#fde8e8', color:'#c04040', fontSize:'13px'}}>{error}</div>}
      {loading && <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>Loading...</div>}

      {!loading && users.map(u => (
        <div key={u.id} style={{...CARD}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'10px',marginBottom:'12px'}}>
            <div style={{flex:1,minWidth:'220px'}}>
              <div style={{fontSize:'15px',fontWeight:'700',color:'#1a2a4a',wordBreak:'break-all'}}>{u.email}</div>
              <div style={{fontSize:'11px',color:'#8aa0c0',marginTop:'4px'}}>ID {u.id} · first seen {fmtDate(u.created_at)}</div>
            </div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {editing === u.id ? (
                <>
                  <button onClick={()=>saveUser(u.id)} style={{...BTN, background:'linear-gradient(135deg,#7ab0f0,#9b8fe8)',color:'white',border:'none'}}>Save</button>
                  <button onClick={()=>setEditing(null)} style={{...BTN}}>Cancel</button>
                </>
              ) : (
                <>
                  <button onClick={()=>{setEditing(u.id); setPendingTier(u.subscription_tier||'free'); setPendingClinician(!!u.is_clinician); setPendingSuperuser(!!u.is_superuser);}} style={{...BTN}}>Edit</button>
                  <button onClick={()=>setConfirmDelete(u.id)} style={{...BTN_DANGER}}>Delete</button>
                </>
              )}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'10px',fontSize:'12px'}}>
            <Field label="Tier">
              {editing===u.id ? (
                <select value={pendingTier} onChange={e=>setPendingTier(e.target.value)} style={{fontSize:'12px',padding:'4px 6px',borderRadius:'6px',border:'1px solid rgba(122,176,240,0.3)'}}>
                  <option value="free">free</option>
                  <option value="monthly">monthly</option>
                  <option value="yearly">yearly</option>
                </select>
              ) : (
                <span style={{fontWeight:'700',color: u.subscription_tier === 'free' ? '#8aa0c0' : '#4a7ad0'}}>{u.subscription_tier || 'free'}</span>
              )}
            </Field>
            <Field label="Subscribed"><span style={{fontWeight:'700',color:u.is_subscribed?'#70b870':'#8aa0c0'}}>{u.is_subscribed ? 'Yes' : 'No'}</span></Field>
            <Field label="Verified"><span style={{color:u.is_verified?'#1a2a4a':'#c04040'}}>{u.is_verified ? 'Yes' : 'No'}</span></Field>
            <Field label="Clinician">
              {editing===u.id ? (
                <label style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',cursor:'pointer'}}>
                  <input type="checkbox" checked={pendingClinician} onChange={e=>setPendingClinician(e.target.checked)}/>
                  <span>{pendingClinician ? 'Yes' : 'No'}</span>
                </label>
              ) : (
                <span style={{color:u.is_clinician?'#4a7ad0':'#8aa0c0',fontWeight:'600'}}>{u.is_clinician ? 'Yes' : 'No'}</span>
              )}
            </Field>
            <Field label="Superuser">
              {editing===u.id ? (
                <label style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',cursor:'pointer'}}>
                  <input type="checkbox" checked={pendingSuperuser} onChange={e=>setPendingSuperuser(e.target.checked)}/>
                  <span>{pendingSuperuser ? 'Yes' : 'No'}</span>
                </label>
              ) : (
                <span style={{color:u.is_superuser?'#c04040':'#8aa0c0',fontWeight:'700'}}>{u.is_superuser ? 'YES' : 'No'}</span>
              )}
            </Field>
            <Field label="Scans"><span style={{fontWeight:'700',color:'#1a2a4a'}}>{u.scan_count}</span></Field>
            <Field label="AI spend"><span style={{color:'#1a2a4a'}}>${(u.monthly_spend||0).toFixed(2)}</span></Field>
          </div>

          {confirmDelete === u.id && (
            <div style={{marginTop:'12px',padding:'12px',background:'#fde8e8',border:'1px solid #f0b0b0',borderRadius:'10px',fontSize:'12px'}}>
              <div style={{color:'#c04040',fontWeight:'700',marginBottom:'8px'}}>Delete {u.email}?</div>
              <div style={{color:'#8a5050',marginBottom:'10px'}}>This cannot be undone. Any tool usage history for this user will also be removed.</div>
              <div style={{display:'flex',gap:'6px'}}>
                <button onClick={()=>deleteUser(u.id)} style={{...BTN_DANGER,background:'#c04040',color:'white',border:'none'}}>Yes, delete</button>
                <button onClick={()=>setConfirmDelete(null)} style={{...BTN}}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {!loading && users.length === 0 && <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>No users found.</div>}
    </div>
  );
};

const Field: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div>
    <div style={{fontSize:'10px',fontWeight:'700',color:'#8aa0c0',textTransform:'uppercase',letterSpacing:'0.6px',marginBottom:'2px'}}>{label}</div>
    <div>{children}</div>
  </div>
);

const FeedbackChart: React.FC<{rows: {tool: string; up: number; down: number; ratio: number; total: number}[]}> = ({rows}) => {
  if (!rows.length) return <div style={{textAlign:'center', color:'#8aa0c0', fontSize:'13px', padding:'20px'}}>No feedback submitted yet.</div>;
  const max = Math.max(...rows.map(r => r.total), 1);
  return (
    <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
      {rows.map((r) => (
        <div key={r.tool} style={{display:'grid', gridTemplateColumns:'110px 1fr 90px', gap:'10px', alignItems:'center'}}>
          <div style={{fontSize:'12px', color:'#1a2a4a', fontWeight:'700', textTransform:'capitalize', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.tool}</div>
          <div style={{position:'relative', height:'16px', background:'rgba(122,176,240,0.08)', borderRadius:'8px', overflow:'hidden', display:'flex'}}>
            <div title={`👍 ${r.up}`} style={{width: `${(r.up / max) * 100}%`, background:'linear-gradient(90deg,#88c8a8,#70b870)', transition:'width 0.3s'}}/>
            <div title={`👎 ${r.down}`} style={{width: `${(r.down / max) * 100}%`, background:'linear-gradient(90deg,#e89898,#c04040)', transition:'width 0.3s'}}/>
          </div>
          <div style={{textAlign:'right', fontSize:'11px', fontWeight:'700'}}>
            <span style={{color:'#70b870'}}>{r.up}</span>
            <span style={{color:'#8aa0c0', margin:'0 3px'}}>·</span>
            <span style={{color:'#c04040'}}>{r.down}</span>
            <span style={{marginLeft:'6px', color:'#4a7ad0'}}>{r.ratio}%</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const BarChart: React.FC<{rows: {label: string; value: number}[]; height?: number; valueSuffix?: string}> = ({rows, height = 140, valueSuffix = ''}) => {
  if (!rows.length) return <div style={{textAlign:'center', color:'#8aa0c0', fontSize:'13px', padding:'20px'}}>No data yet.</div>;
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div style={{display:'flex', alignItems:'flex-end', gap:'3px', height: `${height}px`, paddingTop:'22px', overflowX:'auto'}}>
      {rows.map((r, i) => {
        const h = Math.max(2, Math.round((r.value / max) * (height - 40)));
        return (
          <div key={i} title={`${r.label}: ${r.value}${valueSuffix}`} style={{flex:'1 0 20px', minWidth:'20px', display:'flex', flexDirection:'column', alignItems:'center', gap:'3px'}}>
            <div style={{fontSize:'9px', color:'#4a7ad0', fontWeight:'700', minHeight:'11px'}}>{r.value ? `${r.value}${valueSuffix}` : ''}</div>
            <div style={{width:'100%', height: `${h}px`, background:'linear-gradient(180deg,#7ab0f0,#9b8fe8)', borderRadius:'4px 4px 0 0'}}/>
            <div style={{fontSize:'8px', color:'#8aa0c0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%', textAlign:'center'}}>{r.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const AnalyticsTab: React.FC<TabProps> = ({ API, headers, onUnauthorized }) => {
  const [data, setData] = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);
  const [feedback, setFeedback] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const af = useAuthedFetch(onUnauthorized);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const [sRes, cRes, fRes] = await Promise.all([
          af(`${API}/admin/stats`, { headers }),
          af(`${API}/admin/charts`, { headers }),
          af(`${API}/admin/feedback`, { headers }),
        ]);
        if (!sRes.ok) throw new Error('Failed to load stats');
        setData(await sRes.json());
        if (cRes.ok) setCharts(await cRes.json());
        if (fRes.ok) setFeedback(await fRes.json());
      } catch(e:any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [API, headers, af]);

  if (loading) return <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>Loading...</div>;
  if (error) return <div style={{...CARD, background:'#fde8e8', color:'#c04040', fontSize:'13px'}}>{error}</div>;
  if (!data) return null;

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px',marginBottom:'16px'}}>
        <Stat label="Total users" value={data.users.total}/>
        <Stat label="Verified" value={data.users.verified}/>
        <Stat label="Subscribed" value={data.users.subscribed}/>
        <Stat label="Clinicians" value={data.users.clinicians}/>
        <Stat label="New this week" value={data.users.new_this_week}/>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px',marginBottom:'16px'}}>
        <Stat label="Scans today" value={data.scans.today}/>
        <Stat label="Scans 7d" value={data.scans.this_week}/>
        <Stat label="Scans this month" value={data.scans.this_month}/>
        <Stat label="Scans lifetime" value={data.scans.lifetime}/>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'12px',marginBottom:'16px'}}>
        <Stat label="Revenue est. (mo)" value={`$${data.revenue_month_estimate.toFixed(2)}`} accent/>
        <Stat label="AI spend (mo)" value={`$${data.ai_spend_month.toFixed(2)}`}/>
        <Stat label="Monthly subs" value={data.subscriptions.monthly}/>
        <Stat label="Yearly subs" value={data.subscriptions.yearly}/>
      </div>

      {charts && (
        <>
          <div style={CARD}>
            <div style={LABEL}>MRR by month · last 6 months ($)</div>
            <BarChart rows={(charts.revenue_by_month || []).map((d:any)=>({label: d.month.slice(2), value: Math.round((d.mrr || 0) * 100) / 100}))} height={160} valueSuffix=""/>
          </div>
          <div style={CARD}>
            <div style={LABEL}>New signups · last 30 days</div>
            <BarChart rows={(charts.signups_by_day || []).map((d:any)=>({label: d.date.slice(5), value: d.count}))}/>
          </div>
          <div style={CARD}>
            <div style={LABEL}>AI spend · last 30 days ($)</div>
            <BarChart rows={(charts.ai_spend_by_day || []).map((d:any)=>({label: d.date.slice(5), value: Math.round(d.spend * 100) / 100}))} valueSuffix=""/>
          </div>
          <div style={CARD}>
            <div style={LABEL}>New subscriptions per month</div>
            <BarChart rows={(charts.subs_by_month || []).map((d:any)=>({label: d.month, value: d.count}))}/>
          </div>
          <div style={CARD}>
            <div style={LABEL}>Tool usage · last 30 days</div>
            <BarChart rows={(charts.tool_usage_by_tool || []).map((d:any)=>({label: d.tool, value: d.count}))}/>
          </div>
          <div style={CARD}>
            <div style={LABEL}>NephroAI tab usage · all time</div>
            <BarChart rows={(charts.nephro_breakdown || []).map((d:any)=>({label: d.tab, value: d.count}))}/>
          </div>
          {charts.cases_stats && (
            <div style={CARD}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', flexWrap:'wrap', gap:'8px'}}>
                <div style={LABEL}>Recent cases saved</div>
                <div style={{fontSize:'12px', color:'#6a8ab0'}}>Total <span style={{fontWeight:'800', color:'#1a2a4a'}}>{charts.cases_stats.total}</span>{charts.cases_stats.most_active && <> · most active <span style={{color:'#4a7ad0', fontWeight:'700', textTransform:'capitalize'}}>{charts.cases_stats.most_active}</span></>}</div>
              </div>
              <BarChart rows={(charts.cases_stats.per_tool || []).map((d:any)=>({label: d.tool, value: d.count}))}/>
            </div>
          )}
        </>
      )}

      <div style={CARD}>
        <div style={LABEL}>Tool usage breakdown</div>
        {data.tool_breakdown.length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No scans logged yet.</div>
        ) : data.tool_breakdown.map((t:any) => (
          <div key={t.tool} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px'}}>
            <span style={{color:'#1a2a4a',textTransform:'capitalize'}}>{t.tool}</span>
            <span style={{fontWeight:'700',color:'#4a7ad0'}}>{t.count}</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Most used tools this month</div>
        {(data.most_used_month || []).length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No usage yet this month.</div>
        ) : (data.most_used_month || []).map((t:any) => (
          <div key={t.tool} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px'}}>
            <span style={{color:'#1a2a4a',textTransform:'capitalize'}}>{t.tool}</span>
            <span style={{fontWeight:'700',color:'#4a7ad0'}}>{t.count}</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Feedback ratings per tool · 👍 vs 👎 (legacy)</div>
        <FeedbackChart rows={data.feedback_summary || []}/>
      </div>

      <div style={CARD}>
        <div style={LABEL}>Recent feedback comments</div>
        {!feedback || (feedback.comments || []).length === 0 ? (
          <div style={{fontSize:'13px', color:'#8aa0c0'}}>No written feedback yet.</div>
        ) : (feedback.comments || []).map((c:any) => (
          <div key={c.id} style={{padding:'10px 0', borderBottom:'0.5px solid rgba(0,0,0,0.06)'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'11px', color:'#8aa0c0', marginBottom:'4px'}}>
              <span style={{fontWeight:'700', color:'#4a7ad0', textTransform:'capitalize'}}>{c.tool_slug}</span>
              <span>{c.created_at ? new Date(c.created_at).toLocaleString(undefined,{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}) : ''}</span>
            </div>
            <div style={{fontSize:'13px', color:'#1a2a4a', lineHeight:'1.6', whiteSpace:'pre-wrap', wordBreak:'break-word'}}>{c.comment}</div>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Most active users</div>
        {data.most_active.length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No activity yet.</div>
        ) : data.most_active.map((u:any) => (
          <div key={u.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px',gap:'10px'}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:'#1a2a4a',fontWeight:'600',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.email}</div>
              <div style={{fontSize:'11px',color:'#8aa0c0'}}>{u.tier || 'free'} · ${(u.monthly_spend||0).toFixed(2)} spend</div>
            </div>
            <div style={{fontWeight:'700',color:'#4a7ad0'}}>{u.scan_count}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Stat: React.FC<{label: string; value: any; accent?: boolean}> = ({label, value, accent}) => (
  <div style={{...CARD, marginBottom:0, padding:'16px', background: accent ? 'linear-gradient(135deg,rgba(122,176,240,0.18),rgba(155,143,232,0.18))' : CARD.background}}>
    <div style={LABEL}>{label}</div>
    <div style={{fontSize:'22px',fontWeight:'800',color:'#1a2a4a'}}>{value}</div>
  </div>
);

const HealthTab: React.FC<TabProps> = ({ API, headers, onUnauthorized }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const af = useAuthedFetch(onUnauthorized);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await af(`${API}/admin/health`, { headers });
      if (!res.ok) throw new Error('Failed to load health');
      setData(await res.json());
    } catch(e:any) { setError(e.message); }
    finally { setLoading(false); }
  }, [API, headers, af]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>Loading...</div>;
  if (error) return <div style={{...CARD, background:'#fde8e8', color:'#c04040', fontSize:'13px'}}>{error}</div>;
  if (!data) return null;

  const Item: React.FC<{name: string; ok: boolean; note?: string}> = ({name, ok, note}) => (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'14px'}}>
      <div>
        <div style={{color:'#1a2a4a',fontWeight:'600'}}>{name}</div>
        {note && <div style={{fontSize:'11px',color:'#8aa0c0',marginTop:'2px'}}>{note}</div>}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'12px',fontWeight:'700',color: ok ? '#70b870' : '#c04040'}}>
        <span style={{width:'8px',height:'8px',borderRadius:'50%',background: ok ? '#70b870' : '#c04040'}}/>
        {ok ? 'OK' : 'Down'}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'8px'}}>
        <button onClick={load} style={{...BTN}}>Refresh</button>
      </div>
      <div style={CARD}>
        <div style={LABEL}>System status</div>
        <Item name="API" ok={true} note="You're talking to it right now"/>
        <Item name="Database" ok={data.database.ok} note={data.database.error}/>
        <Item name="SendGrid" ok={data.sendgrid.ok} note={`from: ${data.sendgrid.from_email}`}/>
        <Item name="Stripe API key" ok={data.stripe.ok}/>
        <Item name="Stripe webhook secret" ok={data.stripe.webhook_configured}/>
        <Item name="Anthropic API" ok={data.anthropic.ok}/>
        <Item name="Admin token configured" ok={data.admin_token_configured}/>
      </div>
    </div>
  );
};

const ModerationTab: React.FC<TabProps> = ({ API, headers, onUnauthorized }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const af = useAuthedFetch(onUnauthorized);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await af(`${API}/admin/moderation`, { headers });
        if (!res.ok) throw new Error('Failed to load moderation');
        setData(await res.json());
      } catch(e:any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [API, headers, af]);

  if (loading) return <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>Loading...</div>;
  if (error) return <div style={{...CARD, background:'#fde8e8', color:'#c04040', fontSize:'13px'}}>{error}</div>;
  if (!data) return null;

  return (
    <div>
      <div style={CARD}>
        <div style={LABEL}>Users approaching monthly AI limit (≥80%)</div>
        {data.approaching_limit.length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>All clear.</div>
        ) : data.approaching_limit.map((u:any) => (
          <div key={u.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px',gap:'10px'}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:'#1a2a4a',fontWeight:'600',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.email}</div>
              <div style={{fontSize:'11px',color:'#8aa0c0'}}>{u.tier} · ${u.spend.toFixed(2)} of ${u.limit.toFixed(2)}</div>
            </div>
            <div style={{fontWeight:'700',color: u.pct >= 100 ? '#c04040' : '#d89030'}}>{u.pct.toFixed(1)}%</div>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Heavy usage today (≥10 scans)</div>
        {data.heavy_usage_today.length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No flagged activity.</div>
        ) : data.heavy_usage_today.map((u:any) => (
          <div key={u.id} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px'}}>
            <span style={{color:'#1a2a4a',fontWeight:'600'}}>{u.email}</span>
            <span style={{fontWeight:'700',color:'#4a7ad0'}}>{u.scans_today} today</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Unverified accounts with usage</div>
        {data.unverified_with_usage.length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>None.</div>
        ) : data.unverified_with_usage.map((u:any) => (
          <div key={u.id} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px'}}>
            <span style={{color:'#1a2a4a',fontWeight:'600'}}>{u.email}</span>
            <span style={{fontWeight:'700',color:'#4a7ad0'}}>{u.scan_count} scans</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Suspicious IPs · ≥5 new-account attempts in 24h</div>
        {(data.suspicious_ips || []).length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No suspicious activity detected.</div>
        ) : (data.suspicious_ips || []).map((r:any) => (
          <div key={r.ip_hash_tail} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px'}}>
            <span style={{color:'#1a2a4a', fontFamily:'ui-monospace,monospace'}}>ip:…{r.ip_hash_tail}</span>
            <span style={{fontWeight:'700',color:'#c04040'}}>{r.distinct_new_accounts_24h} new accounts</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Deleted-account re-registration attempts</div>
        {(data.blocklist_rejoin_attempts || []).length === 0 ? (
          <div style={{fontSize:'13px',color:'#8aa0c0'}}>No blocklisted emails have attempted to re-register.</div>
        ) : (data.blocklist_rejoin_attempts || []).map((r:any, i:number) => (
          <div key={i} style={{display:'grid',gridTemplateColumns:'1fr auto auto',gap:'12px',padding:'8px 0',borderBottom:'0.5px solid rgba(0,0,0,0.06)',fontSize:'13px',alignItems:'center'}}>
            <span style={{color:'#1a2a4a', fontFamily:'ui-monospace,monospace'}}>email:…{r.email_hash_tail}</span>
            <span style={{fontSize:'11px',color:'#8aa0c0'}}>{r.deleted_at ? new Date(r.deleted_at).toLocaleDateString() : '—'}</span>
            <span style={{fontWeight:'700',color:'#c04040'}}>{r.attempts} attempt{r.attempts===1?'':'s'}</span>
          </div>
        ))}
      </div>

      <div style={CARD}>
        <div style={LABEL}>Failed payment attempts</div>
        <div style={{fontSize:'13px',color:'#8aa0c0',lineHeight:'1.6'}}>{data.failed_payments.note}</div>
      </div>
    </div>
  );
};

// ───── Visitors tab ─────────────────────────────────────────────────────
// Live page-load tracker. Stat cards + 30-day SVG line chart + top pages,
// top referrers, and a recent feed that auto-refreshes every 60s. Hand-
// rolled SVG chart instead of recharts (not in the dep tree, and the rest
// of the app uses inline SVG for charts too).

const NAVY      = '#1a2a4a';
const NAVY_DEEP = '#0f1a30';
const GOLD      = '#C9A84C';
const GOLD_SOFT = 'rgba(201,168,76,0.14)';

interface VisitsStats {
  total_visits_today: number;
  total_visits_week: number;
  total_visits_month: number;
  total_unique_ips_today: number;
  total_unique_ips_week: number;
  top_pages: { page: string; count: number }[];
  top_referrers: { referrer: string; count: number }[];
  visits_by_day: { date: string; count: number }[];
  recent_visits: {
    ip: string; page: string; referrer: string; country: string;
    region?: string; user_agent?: string; time: string | null;
  }[];
  excluded_ips_count: number;
}

const COUNTRY_FLAGS: Record<string, string> = {
  'United States':'🇺🇸','Canada':'🇨🇦','United Kingdom':'🇬🇧','Australia':'🇦🇺',
  'Germany':'🇩🇪','France':'🇫🇷','Spain':'🇪🇸','Italy':'🇮🇹','Netherlands':'🇳🇱',
  'Brazil':'🇧🇷','Mexico':'🇲🇽','Argentina':'🇦🇷','India':'🇮🇳','Japan':'🇯🇵',
  'South Korea':'🇰🇷','China':'🇨🇳','Singapore':'🇸🇬','Israel':'🇮🇱','Ireland':'🇮🇪',
  'Sweden':'🇸🇪','Norway':'🇳🇴','Denmark':'🇩🇰','Finland':'🇫🇮','Poland':'🇵🇱',
  'Local':'💻','Unknown':'🌐',
};

const timeAgo = (iso: string | null): string => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
};

const VisitorsTab: React.FC<TabProps> = ({ API, headers, onUnauthorized }) => {
  const [data, setData] = useState<VisitsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState('');
  const af = useAuthedFetch(onUnauthorized);

  const load = useCallback(async () => {
    setErr('');
    try {
      const res = await af(`${API}/admin/visitors/stats`, { headers });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const d = await res.json();
      setData(d);
    } catch (e: any) { setErr(e.message || 'Load failed'); }
    finally { setLoading(false); }
  }, [API, headers, af]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh the recent feed + stats every 60s. Light touch.
  useEffect(() => {
    const id = window.setInterval(() => { load(); }, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  const clearOld = async () => {
    if (clearing) return;
    if (!window.confirm('Delete all page-visit rows older than 90 days?')) return;
    setClearing(true); setClearMsg('');
    try {
      const res = await af(`${API}/admin/visitors/clear`, { method:'DELETE', headers });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const d = await res.json();
      setClearMsg(`Deleted ${d.deleted} old row${d.deleted === 1 ? '' : 's'}.`);
      load();
      window.setTimeout(() => setClearMsg(''), 4000);
    } catch (e: any) { setClearMsg(e.message || 'Clear failed.'); }
    finally { setClearing(false); }
  };

  if (loading && !data) return <div style={{...CARD, textAlign:'center', color:'#8aa0c0'}}>Loading visitors…</div>;
  if (err) return <div style={{...CARD, background:'#fde8e8', color:'#c04040', fontSize:'13px'}}>{err}</div>;
  if (!data) return null;

  const totalTopPages = data.top_pages.reduce((s, p) => s + p.count, 0) || 1;

  return (
    <div>
      {/* Stat cards */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'12px', marginBottom:'16px'}}>
        <StatCard label="Visits today"     value={data.total_visits_today.toLocaleString()} accent="navy"/>
        <StatCard label="Visits this week" value={data.total_visits_week.toLocaleString()}  accent="navy"/>
        <StatCard label="Visits this month"value={data.total_visits_month.toLocaleString()} accent="gold"/>
        <StatCard label="Unique IPs today" value={data.total_unique_ips_today.toLocaleString()} sub={`${data.total_unique_ips_week} this week`} accent="navy"/>
      </div>

      {/* Line chart */}
      <div style={{...CARD}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'10px'}}>
          <div style={{fontSize:'14px', fontWeight:800, color: NAVY}}>Visits over time</div>
          <div style={{fontSize:'11px', color:'#8aa0c0'}}>last 30 days</div>
        </div>
        <VisitsLineChart series={data.visits_by_day}/>
      </div>

      {/* Top pages + referrers */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'12px'}}>
        <div style={{...CARD}}>
          <div style={{fontSize:'14px', fontWeight:800, color: NAVY, marginBottom:'10px'}}>Top pages</div>
          {data.top_pages.length === 0 ? (
            <div style={{fontSize:'12px', color:'#8aa0c0', fontStyle:'italic'}}>No traffic yet.</div>
          ) : (
            <div>
              <RowHead cols={['Page','Visits','% of total']}/>
              {data.top_pages.map(p => (
                <Row3 key={p.page}
                  cols={[
                    <span style={{fontFamily:'ui-monospace, monospace', fontSize:'12px', color: NAVY}}>{p.page}</span>,
                    <span style={{fontWeight:700, color: NAVY}}>{p.count.toLocaleString()}</span>,
                    <span style={{color:'#8aa0c0'}}>{((p.count / totalTopPages) * 100).toFixed(1)}%</span>,
                  ]}/>
              ))}
            </div>
          )}
        </div>

        <div style={{...CARD}}>
          <div style={{fontSize:'14px', fontWeight:800, color: NAVY, marginBottom:'10px'}}>Top referrers</div>
          {data.top_referrers.length === 0 ? (
            <div style={{fontSize:'12px', color:'#8aa0c0', fontStyle:'italic'}}>No referrer data yet.</div>
          ) : (
            <div>
              <RowHead cols={['Referrer','Count']}/>
              {data.top_referrers.map(r => (
                <Row2 key={r.referrer}
                  cols={[
                    <span style={{fontSize:'12px', color: NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'inline-block', maxWidth:'100%'}}>
                      {r.referrer === 'Direct' ? <span style={{color: GOLD, fontWeight:700}}>● Direct</span> : r.referrer}
                    </span>,
                    <span style={{fontWeight:700, color: NAVY}}>{r.count.toLocaleString()}</span>,
                  ]}/>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent feed */}
      <div style={{...CARD, marginTop:'12px'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'10px'}}>
          <div style={{fontSize:'14px', fontWeight:800, color: NAVY}}>Recent visits</div>
          <div style={{fontSize:'11px', color:'#8aa0c0'}}>auto-refreshes every 60s</div>
        </div>
        {data.recent_visits.length === 0 ? (
          <div style={{fontSize:'12px', color:'#8aa0c0', fontStyle:'italic', padding:'14px 0'}}>No recent visits.</div>
        ) : (
          <div style={{maxHeight:'420px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'4px'}}>
            {data.recent_visits.map((v, i) => (
              <div key={i} style={{display:'grid', gridTemplateColumns:'72px 130px 1fr 1fr 80px', gap:'10px', alignItems:'center', padding:'8px 10px', background:'#FAFAFE', borderRadius:'8px', border:'0.5px solid rgba(83,74,183,0.08)'}}>
                <span style={{fontSize:'14px'}} title={v.country}>
                  {COUNTRY_FLAGS[v.country] || '🌐'} <span style={{fontSize:'10px', color:'#8aa0c0', fontWeight:700, marginLeft:'2px'}}>{(v.country || '').slice(0,2).toUpperCase()}</span>
                </span>
                <span style={{fontFamily:'ui-monospace, monospace', fontSize:'11px', color:'#6a8ab0'}}>{v.ip}</span>
                <span style={{fontFamily:'ui-monospace, monospace', fontSize:'12px', color: NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={v.page}>{v.page}</span>
                <span style={{fontSize:'11px', color:'#8aa0c0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={v.referrer}>
                  {v.referrer === 'Direct' ? <span style={{color: GOLD, fontWeight:700}}>● Direct</span> : v.referrer}
                </span>
                <span style={{fontSize:'10px', color:'#8aa0c0', textAlign:'right', whiteSpace:'nowrap'}}>{timeAgo(v.time)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Excluded IPs notice + admin */}
      <div style={{...CARD, marginTop:'12px', background:`linear-gradient(135deg, ${NAVY_DEEP}, ${NAVY})`, color:'white', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px', flexWrap:'wrap'}}>
        <div style={{fontSize:'12px', lineHeight:1.6, flex:1, minWidth:'240px'}}>
          <div style={{fontSize:'10px', letterSpacing:'1.5px', textTransform:'uppercase', color: GOLD, fontWeight:800, marginBottom:'4px'}}>
            Privacy · {data.excluded_ips_count} IP{data.excluded_ips_count === 1 ? '' : 's'} excluded
          </div>
          Your IP and superuser sessions are excluded from tracking. To exclude additional IPs, add them to <code style={{background: GOLD_SOFT, padding:'1px 5px', borderRadius:'4px', color: GOLD}}>EXCLUDED_IPS</code> in Railway variables (comma-separated).
        </div>
        <button onClick={clearOld} disabled={clearing}
          style={{background: GOLD, color: NAVY_DEEP, border:'none', borderRadius:'10px', padding:'8px 14px', fontSize:'12px', fontWeight:800, cursor: clearing ? 'wait' : 'pointer', letterSpacing:'0.4px', textTransform:'uppercase', opacity: clearing ? 0.6 : 1}}>
          {clearing ? 'Clearing…' : 'Clear visits > 90 days'}
        </button>
      </div>
      {clearMsg && (
        <div style={{...CARD, marginTop:'10px', background:'rgba(112,184,112,0.10)', color:'#1f6633', fontSize:'12px', padding:'10px 14px'}}>{clearMsg}</div>
      )}
    </div>
  );
};

const StatCard: React.FC<{label: string; value: string; sub?: string; accent: 'navy' | 'gold'}> = ({ label, value, sub, accent }) => (
  <div style={{...CARD, padding:'16px', position:'relative', overflow:'hidden'}}>
    <div style={{fontSize:'10px', letterSpacing:'1.4px', textTransform:'uppercase', color:'#8aa0c0', fontWeight:800}}>{label}</div>
    <div style={{fontSize:'26px', fontWeight:900, color: NAVY, marginTop:'6px', letterSpacing:'-0.4px', lineHeight:1.1}}>{value}</div>
    {sub && <div style={{fontSize:'11px', color:'#8aa0c0', marginTop:'4px'}}>{sub}</div>}
    <div aria-hidden style={{position:'absolute', bottom:0, left:0, right:0, height:'3px', background: accent === 'gold' ? `linear-gradient(90deg, ${GOLD}, #f8c870)` : 'linear-gradient(90deg,#7ab0f0,#9b8fe8)'}}/>
  </div>
);

const RowHead: React.FC<{cols: string[]}> = ({ cols }) => (
  <div style={{display:'grid', gridTemplateColumns: cols.length === 3 ? '1fr 80px 80px' : '1fr 80px', gap:'10px', padding:'8px 10px', borderBottom:'0.5px solid rgba(83,74,183,0.12)', fontSize:'10px', fontWeight:800, color:'#8aa0c0', textTransform:'uppercase', letterSpacing:'0.5px'}}>
    {cols.map(c => <div key={c} style={{textAlign: c === 'Page' || c === 'Referrer' ? 'left' : 'right'}}>{c}</div>)}
  </div>
);
const Row3: React.FC<{cols: React.ReactNode[]}> = ({ cols }) => (
  <div style={{display:'grid', gridTemplateColumns:'1fr 80px 80px', gap:'10px', padding:'8px 10px', borderBottom:'0.5px solid rgba(83,74,183,0.06)', alignItems:'center', fontSize:'12px'}}>
    <div style={{minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{cols[0]}</div>
    <div style={{textAlign:'right'}}>{cols[1]}</div>
    <div style={{textAlign:'right'}}>{cols[2]}</div>
  </div>
);
const Row2: React.FC<{cols: React.ReactNode[]}> = ({ cols }) => (
  <div style={{display:'grid', gridTemplateColumns:'1fr 80px', gap:'10px', padding:'8px 10px', borderBottom:'0.5px solid rgba(83,74,183,0.06)', alignItems:'center', fontSize:'12px'}}>
    <div style={{minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{cols[0]}</div>
    <div style={{textAlign:'right'}}>{cols[1]}</div>
  </div>
);

// SVG line chart — last 30 days. Hand-rolled because recharts isn't in the
// dep tree and the rest of the codebase uses inline SVG for charts. Gold
// line + soft filled area underneath.
const VisitsLineChart: React.FC<{series: {date: string; count: number}[]}> = ({ series }) => {
  const W = 720;          // viewBox width
  const H = 180;          // viewBox height
  const PADL = 36, PADR = 12, PADT = 14, PADB = 26;
  const max = Math.max(1, ...series.map(s => s.count));
  const innerW = W - PADL - PADR;
  const innerH = H - PADT - PADB;
  const x = (i: number) => PADL + (series.length <= 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
  const y = (v: number) => PADT + innerH - (v / max) * innerH;

  const linePath = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.count).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${x(series.length - 1).toFixed(1)},${(PADT + innerH).toFixed(1)} L${x(0).toFixed(1)},${(PADT + innerH).toFixed(1)} Z`;

  // Y-axis ticks: 0, max/2, max
  const ticks = [0, Math.round(max / 2), max];
  // X-axis labels: every 5 days
  const xLabels = series.map((s, i) => ({ s, i })).filter(({i}) => i % 5 === 0 || i === series.length - 1);

  return (
    <div style={{width:'100%', overflowX:'auto'}}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{width:'100%', height:'200px', display:'block'}} aria-label="Visits over the last 30 days">
        {/* Grid lines */}
        {ticks.map(t => (
          <line key={t} x1={PADL} x2={W - PADR} y1={y(t)} y2={y(t)} stroke="rgba(83,74,183,0.10)" strokeDasharray="3 4"/>
        ))}
        {ticks.map(t => (
          <text key={`yl-${t}`} x={PADL - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#8aa0c0" fontFamily="ui-sans-serif, system-ui">{t.toLocaleString()}</text>
        ))}
        {/* Area */}
        <defs>
          <linearGradient id="visits-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={GOLD} stopOpacity="0.32"/>
            <stop offset="100%" stopColor={GOLD} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#visits-area)"/>
        {/* Line */}
        <path d={linePath} fill="none" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
        {/* Dots on each point with a tooltip via <title> */}
        {series.map((s, i) => (
          <g key={s.date}>
            <circle cx={x(i)} cy={y(s.count)} r="2.5" fill={GOLD}>
              <title>{s.date}: {s.count} visit{s.count === 1 ? '' : 's'}</title>
            </circle>
          </g>
        ))}
        {/* X labels */}
        {xLabels.map(({s, i}) => (
          <text key={`xl-${s.date}`} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#8aa0c0" fontFamily="ui-sans-serif, system-ui">
            {s.date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
};

export default Admin;
